/**
 * Global styles for Bower application
 * Sets base styles that apply across the entire app
 */

export const styles = {
  global: () => ({
    // Base HTML elements with semantic tokens for dark mode support
    'html, body': {
      // Neutralize Mantine's global `body { color: var(--mantine-color-text) }` from
      // @mantine/core/styles.css (imported by DatePicker). Without this, Mantine's
      // dark-unaware default overrides Chakra's dark mode text color to black.
      '--mantine-color-text': 'var(--chakra-colors-text-primary)',
      bg: 'bg.canvas',
      color: 'text.primary',
      lineHeight: 'base',
      fontFamily: 'body',
      fontSize: 'md',
      // Structural guard for the licensed-weight gap. GT Ultra Median is
      // licensed in Regular ONLY, and 17 call sites across the app pair
      // `fontFamily="heading|display"` with a bold weight. Left alone, the
      // browser fakes those by smearing the outline — on a high-contrast
      // display face that reads as a font-loading fault, not as emphasis.
      //
      // `font-synthesis-weight: none` makes the miss render as the real
      // Regular instead: hierarchy flattens where a bold was intended, but
      // nothing ever looks broken, and NEW code cannot reintroduce the ugly
      // failure mode. Weight only — style synthesis is left on, because no
      // italic is licensed for either family and losing italic emphasis
      // silently would be a worse trade.
      //
      // Delete this if a GT Ultra Median bold cut is ever licensed, and fix
      // the call sites instead (list in the PR description).
      fontSynthesisWeight: 'none',
      transition: 'background-color 0.2s, color 0.2s',
      // Mobile-specific improvements
      height: '100%',
      overflow: 'hidden',
      position: 'relative',
      // Prevent pull-to-refresh and overscroll
      overscrollBehavior: 'none',
      WebkitOverflowScrolling: 'touch',
      // Mobile viewport fix - use dynamic viewport height to exclude browser chrome
      '@media (max-width: 767px)': {
        height: '100dvh',
        fontSize: '16px', // Prevents input-focus zoom on mobile browsers
        touchAction: 'manipulation', // Removes 300ms click delay on touch devices
        overscrollBehaviorX: 'none', // Prevent horizontal rubber-banding / swipe-nav
      },
    },

    // Root element
    '#root': {
      minHeight: '100vh',
      height: '100vh',
      bg: 'bg.canvas',
      overflow: 'hidden',
      position: 'relative',
      // Mobile viewport height fix - use dynamic viewport to account for browser UI
      '@media (max-width: 767px)': {
        height: '100dvh', // Dynamic viewport height excludes browser chrome
        minHeight: '100dvh',
      },
    },

    // Print. The app shell is a fixed-height scroller: html/body/#root and
    // every Layout box down to the page are 100vh with overflow hidden or
    // auto, and nothing above this line has ever had a print rule. Paged
    // media does not scroll, so Cmd-P on a note laid out exactly one
    // viewport of content and cut the rest (a customer could not print a
    // protocol before a lab session). Screen media never enters this block.
    //
    // The hooks are attributes, not selectors on Chakra classes, so the
    // shell can be restructured without this block silently missing a box:
    //   data-print="scroller"  every fixed-height / overflow container
    //                          between #root and the editor
    //   data-print="hide"      chrome that has no meaning on paper (rail,
    //                          panel column, action buttons)
    // `!important` throughout: each of these overrides an emotion class on
    // the element itself, which out-orders a global rule at equal weight.
    '@media print': {
      'html, body, #root': {
        height: 'auto !important',
        maxHeight: 'none !important',
        overflow: 'visible !important',
      },
      '[data-print="scroller"]': {
        height: 'auto !important',
        minHeight: '0 !important',
        maxHeight: 'none !important',
        overflow: 'visible !important',
        position: 'static !important',
      },
      // The page is pushed across by the rail (+ an open panel); both are
      // hidden on paper, so the margin they reserved goes too.
      '[data-testid="layout-content"]': {
        marginLeft: '0 !important',
      },
      '[data-print="hide"]': {
        display: 'none !important',
      },
      // Wide tables scroll inside their wrapper on screen; on paper the
      // wrapper would clip them instead. Code blocks likewise scroll
      // horizontally, so they wrap.
      '.tiptap-editor .tableWrapper': {
        overflow: 'visible !important',
      },
      '.tiptap-editor pre': {
        overflow: 'visible !important',
        whiteSpace: 'pre-wrap !important',
      },
    },

    // Focus styles — `:focus-visible` (keyboard focus) only so mouse
    // clicks on menu items / buttons don't leave a sticky blue ring.
    // `:focus-visible` matches the modern WCAG pattern: keyboard users
    // see the ring, mouse users don't.
    '*:focus': {
      outline: 'none',
    },
    '*:focus-visible': {
      outline: 'none',
      // The `outline` shadow token — accent.600, chosen so the ring clears
      // WCAG 1.4.11's 3:1 against every surface it can be drawn on, including
      // the sidebar and an active nav row. See the token in theme/index.ts.
      boxShadow: 'outline',
    },

    /**
     * Focus rings on a painted backdrop.
     *
     * `shadows.outline` was tuned to clear WCAG 1.4.11 (3:1) against each flat
     * surface the app paints, and that is NO LONGER TRUE in dark: it is
     * accent.600 with no `_dark` counterpart, so it reads 2.865:1 on
     * `bg.subtle` / `bg.muted`, 2.597 on `bg.hover` and 2.158 on `bg.active`.
     * The first of those crossed 3:1 on 2026-09-22 when the quiet tint was
     * separated from `bg.surface`; the other two were already under. Pinned as
     * a DOCUMENTS case in `brandContrast.test.ts` and deferred there, because
     * the fix is a dark ring and that restyles every focused control.
     *
     * The reasoning below is unaffected either way. The onboarding shape field
     * is not a flat surface at all:
     * it is a shader whose colour changes per pixel and per second, and the
     * plain ring measures 2.19:1 over it in dark mode. So anything focusable
     * inside the onboarding shell gets the two-tone ring, which carries its own
     * adjacent colour and is therefore backdrop-independent.
     *
     * Scoped off the backdrop element rather than a route, because CSS cannot
     * see routes — the sibling combinator reaches exactly the header and body
     * that the backdrop is painted behind. A plain `~` is used rather than
     * `:has()` so that the rule cannot fail silently on an older engine, which
     * for an accessibility fix matters more than the tidier selector.
     *
     * **`:focus-visible` is doubled deliberately — do not "simplify" it.**
     * `components.ts` gives Button a `_focusVisible: { boxShadow: 'outline' }`,
     * which emotion emits as `.css-xxx:focus-visible`. That scores (0,2,0),
     * exactly as a single `:focus-visible` here does, and emotion inserts
     * component styles AFTER global ones — so the component wins the tie and
     * the ring silently reverts on every Chakra Button and IconButton, which
     * is what onboarding's Continue and Back are. Repeating the pseudo-class
     * costs nothing and takes this to (0,3,0), so it wins outright instead of
     * depending on stylesheet insertion order.
     *
     * **Opt out with `data-focus-ring`.** The selector is structural — it
     * reaches every focusable node under the shell — but the REASON for the
     * two-tone ring is not: it is for controls that sit straight on the shader
     * and need a ring OF THEIR OWN. Where a control's focus affordance is
     * something else, winning the specificity contest against it is a
     * regression rather than a fix, and the contest is one this rule always
     * wins, because it was built to. Both of onboarding's cases were overridden
     * before anyone noticed:
     *
     *  - `delegated` — an ANCESTOR draws the affordance. The Ask Bird composer
     *    is a `bg.surface` group that lights its border on `:focus-within`,
     *    wrapping a `variant="unstyled"` input whose own `_focusVisible` sets
     *    `boxShadow: 'none'`. At (0,2,0) that override lost and a second ring
     *    drew INSIDE the group's border.
     *  - `none` — the element is not user-operable and needs NO affordance. The
     *    `onboarding-step-content` wrapper is a `tabIndex={-1}` programmatic
     *    focus target, given no ring on purpose by #1210. `:focus-visible`
     *    reaches it anyway: a script-focused element inherits the match from
     *    whatever was focused before it, so after a KEYBOARD Continue the ring
     *    drew around the whole step region. Only after a keyboard one, which is
     *    why every test missed it — they all click.
     *
     * Matched on PRESENCE, not on the value. The values are documentation, and
     * a third one must not silently fail to opt out because a value-exact
     * predicate did not know about it.
     *
     * Marking is safe for the contest above: `:not()` carries the specificity
     * of its argument, so an attribute selector takes these to (0,4,0). The
     * Button rule is beaten by more than before, never by less.
     *
     * NOT mechanically enforced: nothing detects a control that should carry
     * this and does not. The two above are pinned by tests; a third has to be
     * spotted by hand.
     */
    '[data-onboarding-backdrop] ~ * :focus-visible:focus-visible:not([data-focus-ring]), [data-onboarding-backdrop] ~ *:focus-visible:focus-visible:not([data-focus-ring])':
      {
        boxShadow: 'outlineOnPattern',
      },

    /**
     * Validation messages on a painted backdrop.
     *
     * Same class as the focus ring above, and reachable on the very first
     * screen: `YouStep` renders a `FormErrorMessage` under each required field
     * (a one-character name is enough), and below `lg` the form column
     * deliberately drops its scrim so phones get a background at all — so that
     * text lands on the bare shader.
     *
     * Error inks are the most saturated in the palette and the least forgiving
     * over pigment. `text.error` measures 6.40:1 on the flat dark canvas and
     * 3.37:1 over the field, so the shape field turned a passing surface into a
     * failing one. Dialling `INTENSITY` down far enough to carry it would need
     * roughly 0.13 — about half the field — which is a large permanent cost for
     * one small element.
     *
     * So do what the two-tone ring does: restore a known adjacent colour rather
     * than chase a better one. An opaque `bg.canvas` plate puts the message
     * back on exactly the surface its contrast was designed against, at any
     * field intensity and in both modes.
     *
     * `display: table` shrink-wraps the plate to the text. The element is a
     * flex child of `FormControl`, so it would otherwise stretch the full
     * column width and read as a filled bar rather than a label.
     */
    '[data-onboarding-backdrop] ~ * .chakra-form__error-message': {
      display: 'table',
      backgroundColor: 'bg.canvas',
      borderRadius: 'sm',
      paddingInline: '1',
      marginInlineStart: '-1',
    },

    // Links — primary text color with persistent subtle underline for accessibility.
    // Underline ensures links are distinguishable without color alone (WCAG 1.4.1).
    // Exclude buttons rendered as anchors (e.g. Button as={RouterLink} in sidebar).
    //
    // ONE link treatment for the whole app. Anything that should look like a
    // link gets it from here rather than restyling an anchor locally: this
    // selector scores (0,2,1) — one type plus the two `:not()`s, each carrying
    // its argument's weight — and a Chakra style prop is a single emotion class
    // at (0,1,0), so a local override silently loses the cascade and renders as
    // ordinary body text with no error anywhere. Bird's chat links shipped that
    // way. Change the treatment here AND in the matching `Link` baseStyle in
    // components.ts — globalLinkStyles.test.ts fails if the two disagree.
    // `[data-link-plain]` is the OPT-OUT. A local style prop cannot win against
    // this rule — the selector scores (0,2,1) and a Chakra prop compiles to one
    // emotion class at (0,1,0) — so an anchor that must not look like a link
    // marks itself and the rule stands aside. That is deliberately an attribute
    // listed here rather than a `&&` specificity trick at the call site. `&&`
    // does not even work: doubling the class reaches (0,2,0), which still loses
    // to this rule's (0,2,1) on the type selector — measured, `&&&` is the
    // first one that wins. And a trick that DOES work still reads as a typo to
    // the next person (catalogue CR-045).
    'a:not(.chakra-button):not([data-link-plain])': {
      color: 'text.primary',
      // Colour can't carry a link here — the spec is "links are ink, not the
      // brand blue" (see the `text.link` token) — and a hairline underline
      // alone doesn't register while scanning a paragraph, so links take the
      // emphasis weight.
      //
      // NOTE: with Suisse Int'l licensed at 400/600 only, `medium` and a
      // `<strong>`'s 700 both resolve to the SAME 600 face, so a link and bold
      // prose now render at one weight — the underline is what distinguishes
      // them. That is a real loss of hierarchy inside bold sentences; revisit
      // if Suisse Int'l Medium is ever licensed.
      fontWeight: 'medium',
      // …but a link INSIDE bold or a heading must not go the other way. A
      // declaration always beats inheritance, so `**see the [protocol](/x)
      // first**` used to render the anchor lighter than the <strong> around it
      // — the link was the LIGHTEST thing in the sentence, the opposite of an
      // affordance. Bird's answers produce that shape routinely. Inheriting
      // also keeps this correct in a heading, where the display face has only
      // one cut.
      'strong &, b &, h1 &, h2 &, h3 &, h4 &, h5 &, h6 &': {
        fontWeight: 'inherit',
      },
      textDecoration: 'underline',
      // Derived from the TEXT colour, not `border.default`. Measured in a real
      // browser: border.default (#ECEAE8) on a white page is ~1.27:1 — an
      // underline nobody can see, which is what "there is no underline" meant.
      // 50% of the ink measures 3.3:1 (light) / 4.8:1 (dark), clearing the 3:1
      // non-text threshold — which matters because this underline IS the
      // non-colour cue WCAG 1.4.1 requires, so it has to be perceivable to do
      // its job. At 1px it is still a hairline. Tracks any link colour.
      textDecorationColor: 'color-mix(in srgb, currentColor 50%, transparent)',
      textUnderlineOffset: '3px',
      textDecorationThickness: '1px',
      transition: 'color 0.15s, text-decoration-color 0.15s',
      // Ensure child Text/p elements inherit link color (Chakra Text baseStyle overrides)
      '& .chakra-text, & p': {
        color: 'inherit',
      },
      _hover: {
        color: 'text.accent',
        textDecorationColor: 'currentColor',
      },
    },

    // Data tables use row hover, cursor, and compact layout to communicate
    // navigation. Keeping prose-style underlines there makes titles noisy and
    // also paints a stray underline beneath inline lock badges. Scope the
    // exception to the shared DataTable marker; ordinary links retain the
    // accessible app-wide treatment above.
    '[data-table-view] a:not(.chakra-button)': {
      textDecoration: 'none',
      _hover: {
        textDecoration: 'none',
      },
    },

    // Ensure smooth transitions for color mode changes
    '*': {
      transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
    },

    // Global text selection styling — the brand twilight (#5C88DA) at low
    // alpha. Written as a literal rgba rather than a token because `::selection`
    // is a global rule outside any component's emotion scope; keep it in step
    // with `accent.500` in theme/colors.ts by hand.
    '::selection': {
      backgroundColor: 'rgba(92, 136, 218, 0.18)',
      color: 'inherit',
    },
    '::-moz-selection': {
      backgroundColor: 'rgba(92, 136, 218, 0.18)',
      color: 'inherit',
    },

    // Keep toasts clear of the background-tasks tray (UploadTray). Both are
    // fixed to the bottom-right corner, so without this they overlap. The tray
    // publishes its live height (+ a gap) as `--upload-tray-offset` on the root
    // element; the bottom-anchored Chakra toast containers translate up by that
    // amount so toasts always stack ABOVE the tray, whether it's collapsed or
    // expanded. Defaults to 0px (no tray) → toasts sit in the corner as before.
    // Chakra names each container `chakra-toast-manager-<position>`.
    '#chakra-toast-manager-bottom, #chakra-toast-manager-bottom-right, #chakra-toast-manager-bottom-left':
      {
        transform: 'translateY(calc(-1 * var(--upload-tray-offset, 0px)))',
        transition: 'transform 0.2s ease',
      },

    // Mobile toast improvements:
    // - Larger close button for WCAG 44x44px touch targets
    // - Safe area padding to avoid edge gesture zones (swipe-back, swipe-forward)
    '.chakra-toast': {
      '@media (max-width: 767px)': {
        paddingLeft: 'max(env(safe-area-inset-left, 8px), 8px)',
        paddingRight: 'max(env(safe-area-inset-right, 8px), 8px)',
        '& button[aria-label="Close"]': {
          minWidth: '44px',
          minHeight: '44px',
          padding: '10px',
        },
      },
    },

    // Toast styling overrides - fix transparency and text color
    '.chakra-alert': {
      // Ensure opaque background for all toasts
      backgroundColor: 'var(--chakra-colors-bg-surface) !important',
      // Dark mode specific overrides
      _dark: {
        backgroundColor: 'var(--chakra-colors-gray-800) !important',
        color: 'white !important',
        '& .chakra-alert__title': {
          color: 'white !important',
        },
        '& .chakra-alert__desc': {
          color: 'white !important',
        },
        '& *': {
          color: 'white !important',
        },
      },
    },

    // Dialog/Modal close button - white in dark mode
    '.chakra-modal__close-btn': {
      _dark: {
        color: 'white !important',
        '& svg': {
          fill: 'white !important',
        },
      },
    },

    // AlertDialog text color management - centralized dark mode fixes
    '.chakra-alert-dialog__header': {
      _dark: {
        color: 'var(--chakra-colors-text-primary) !important',
        '& *': {
          color: 'var(--chakra-colors-text-primary) !important',
        },
      },
    },

    '.chakra-alert-dialog__body': {
      _dark: {
        color: 'var(--chakra-colors-text-primary) !important',
        '& *': {
          color: 'var(--chakra-colors-text-primary) !important',
        },
        '& .chakra-text': {
          color: 'var(--chakra-colors-text-primary) !important',
        },
      },
    },

    // AlertDialog close button
    '.chakra-alert-dialog__close-btn': {
      _dark: {
        color: 'white !important',
        '& svg': {
          fill: 'white !important',
        },
      },
    },

    // KaTeX's own rule for its hidden MathML copy, copied from katex.min.css.
    // That sheet now loads on demand (utils/katexStyles.ts), so without this a
    // formula that renders before it lands shows twice. Kept identical to the
    // original; katexStyles.test.tsx fails if they drift.
    '.katex .katex-mathml': {
      border: 0,
      WebkitClipPath: 'inset(50%)',
      clipPath: 'inset(50%)',
      height: '1px',
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      width: '1px',
    },

    // PostHog survey widget — hide branding footer
    '.footer-branding': {
      display: 'none !important',
    },

    // Pulse animation for recording indicator
    '@keyframes pulse': {
      '0%, 100%': {
        opacity: 1,
      },
      '50%': {
        opacity: 0.5,
      },
    },
  }),
};
